import { Check, Info } from "lucide-react";

import { Button } from "components/products/muggleTestV0/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/products/muggleTestV0/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/products/muggleTestV0/components/ui/tooltip";
import { MUGGLE_CUSTOMER_FACING_EMAILS } from "constants/muggleCustomerFacingEmail";
import {
  PricingPlanCtaActionEnum,
  PricingPlanPriceDisplayEnum,
  type PricingPlan,
} from "types/landing/landing.types";

interface PricingCardProps {
  /** Plan content model from `PRICING_PLANS`. */
  plan: PricingPlan;
  /** Invoked for self-serve signup CTAs. Receives the plan so the caller knows which tier was clicked. */
  onSignupClick: (plan: PricingPlan) => void;
  /** Invoked for contact-sales CTAs. */
  onContactSalesClick: () => void;
}

function getExternalCtaHref(plan: PricingPlan): string {
  if (!plan.ctaHref) {
    throw new Error(`Missing ctaHref for external link plan: ${plan.name}`);
  }
  return plan.ctaHref;
}

export function PricingCard(props: PricingCardProps) {
  const { plan, onSignupClick, onContactSalesClick } = props;
  const isExternalCta = plan.ctaAction === PricingPlanCtaActionEnum.EXTERNAL_LINK;
  const isContactSalesCta = plan.ctaAction === PricingPlanCtaActionEnum.CONTACT_SALES;
  const isContactSalesDisplay = plan.priceDisplay === PricingPlanPriceDisplayEnum.CONTACT_SALES;

  return (
    <Card className={`relative flex flex-col border-2 ${plan.popular ? "border-accent/50 shadow-2xl" : "border-border"}`}>
      {plan.popular && plan.badgeLabel && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="inline-flex rounded-full bg-accent px-4 py-1 text-xs font-semibold text-accent-foreground">
            {plan.badgeLabel}
          </span>
        </div>
      )}
      <CardHeader className="pb-8 pt-8">
        <div className="mb-2 flex items-start justify-between gap-2">
          <CardTitle className="text-2xl">{plan.name}</CardTitle>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={`${plan.name} plan details`}
              >
                <Info className="h-4 w-4" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm p-3 text-left">
              <p className="whitespace-pre-wrap text-xs leading-relaxed">{plan.detailsTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Best for</p>
        <p className="text-sm text-muted-foreground">{plan.description}</p>
        {isContactSalesDisplay ? (
          <div className="mt-4 space-y-1">
            <p className="text-sm text-muted-foreground">Contact us for pricing</p>
            <p className="text-base font-medium text-foreground">{MUGGLE_CUSTOMER_FACING_EMAILS.ENTERPRISE_SALES}</p>
          </div>
        ) : (
          <div className="mt-4 flex items-baseline gap-1">
            <span className="text-4xl font-bold tracking-tight">{plan.price}</span>
            {plan.period && <span className="text-muted-foreground">{plan.period}</span>}
          </div>
        )}
      </CardHeader>

      <CardContent className="flex flex-1 flex-col">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Includes</p>
        <ul className="mb-8 flex-1 space-y-3">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              <span className="text-sm text-muted-foreground">{feature}</span>
            </li>
          ))}
        </ul>
        {plan.trustLine && <p className="mb-3 text-center text-xs text-muted-foreground">{plan.trustLine}</p>}
        <Button
          size="lg"
          className={`w-full ${plan.popular ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}`}
          variant={plan.popular ? "default" : "outline"}
          asChild={isExternalCta}
          onClick={
            isContactSalesCta
              ? onContactSalesClick
              : plan.ctaAction === PricingPlanCtaActionEnum.SIGNUP
                ? () => onSignupClick(plan)
                : undefined
          }
        >
          {isExternalCta ? <a href={getExternalCtaHref(plan)}>{plan.cta}</a> : plan.cta}
        </Button>
      </CardContent>
    </Card>
  );
}
